
import React, { useEffect, useState, useCallback } from "react";
import "./VisitorLogsDashboard.css";
import { Navigate } from "react-router-dom";


function VisitorLogsDashboard({ darkMode }) {
  // Superadmin-only protection
  const role = localStorage.getItem("role");
 
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [deleteStatus, setDeleteStatus] = useState("");
  const [exportStatus, setExportStatus] = useState("");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

  const fetchLogs = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/visitor-logs?limit=100`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`
      }
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setLogs(data);
        } else {
          setLogs([]);
          setError(data.error || "Failed to load visitor logs");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load visitor logs");
        setLoading(false);
      });
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchLogs();
    // Listen for global data refresh
    const handler = () => fetchLogs();
    window.addEventListener('dataChanged', handler);
    return () => window.removeEventListener('dataChanged', handler);
  }, [fetchLogs]);


   if (role !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  const handleDelete = async (e) => {
    e.preventDefault();
    setDeleteStatus("");
    if (!from || !to) {
      setDeleteStatus("Please select both dates.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/visitor-logs?from=${from}&to=${to}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        setDeleteStatus(`Deleted ${data.deletedCount} logs.`);
        window.dispatchEvent(new Event('dataChanged'));
      } else {
        setDeleteStatus(data.error || "Delete failed");
      }
    } catch {
      setDeleteStatus("Delete failed");
    }
  };

  const handleExport = async (e) => {
    e.preventDefault();
    setExportStatus("");
    if (!exportFrom || !exportTo) {
      setExportStatus("Please select both dates.");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/visitor-logs/export?from=${exportFrom}&to=${exportTo}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });
      if (!res.ok) {
        setExportStatus("Export failed");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `visitor-logs-${exportFrom}-to-${exportTo}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setExportStatus("Exported successfully.");
    } catch {
      setExportStatus("Export failed");
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    setError("");
    setDeleteStatus("");
    setExportStatus("");
    window.dispatchEvent(new Event('dataChanged'));
  };

  return (
    <div className={`visitor-logs-dashboard${darkMode ? " dark-mode" : ""}`}>
      <h2>Visitor Logs</h2>
      <form className="visitor-logs-delete-form" onSubmit={handleDelete}>
        <label>
          From: <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label>
          To: <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <button type="submit">Delete Logs</button>
        {deleteStatus && <span className="delete-status">{deleteStatus}</span>}
      </form>
      <form className="visitor-logs-export-form" onSubmit={handleExport}>
        <label>
          Export From: <input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} />
        </label>
        <label>
          Export To: <input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} />
        </label>
        <button type="submit">Export Logs</button>
        {exportStatus && <span className="export-status">{exportStatus}</span>}
      </form>
      <button className="visitor-logs-refresh-btn" type="button" onClick={handleRefresh}>
        Refresh
      </button>
      {loading && <p>Loading...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && Array.isArray(logs) && (
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>IP Address</th>
              <th>Device</th>
              <th>Host</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log._id}>
                <td>{new Date(log.date).toLocaleString()}</td>
                <td>{log.ip}</td>
                <td>{log.device}</td>
                <td>{log.hostname}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default VisitorLogsDashboard;
